/**
 * Guards against the "NotFoundError: Failed to execute 'removeChild' on 'Node'"
 * (and the matching insertBefore) crash that browser page-translation triggers.
 *
 * When Google/Chrome (mobile) translates the page, it replaces text nodes with
 * its own <font>-wrapped nodes. React still holds references to the originals,
 * so when it later reconciles and calls removeChild/insertBefore, the node is no
 * longer where React expects it and the DOM throws a NotFoundError (DOMException
 * code 8). Because it happens in React's commit phase, the whole tree unmounts —
 * a white screen (e.g. on /auth/register when the submit button text swaps).
 *
 * These wrappers make the two operations tolerant: if the node has already been
 * moved out from under us, we no-op instead of throwing. Translation keeps
 * working; React just stops crashing on nodes it no longer owns.
 *
 * Refs: facebook/react#11538.
 */
export function installTranslateCrashGuard(): void {
    if (typeof Node !== 'function' || !Node.prototype) {
        return;
    }

    const originalRemoveChild = Node.prototype.removeChild;
    Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
        if (child.parentNode !== this) {
            // The node was already detached/moved (e.g. by the translator).
            return child;
        }
        return originalRemoveChild.call(this, child) as T;
    };

    const originalInsertBefore = Node.prototype.insertBefore;
    Node.prototype.insertBefore = function <T extends Node>(
        this: Node,
        newNode: T,
        referenceNode: Node | null
    ): T {
        if (referenceNode && referenceNode.parentNode !== this) {
            // The reference node isn't ours anymore — append instead of throwing.
            return this.appendChild(newNode) as T;
        }
        return originalInsertBefore.call(this, newNode, referenceNode) as T;
    };
}
